<template>
  <header :class="['kpi-header', { 'kpi-header--draft': isDraft }]">
    <div class="kpi-header__breadcrumbs">
      <ExBreadcrumbs :breadcrumbsList="breadcrumbsList" />
    </div>
    
    <div class="kpi-header__main">
      <div class="kpi-header__left">
        <div class="kpi-header__icon-wrapper">
          <Icon name="kpi_new_icon" class="kpi-header__kpi-icon" />
        </div>
      </div>

      <div class="kpi-header__center">
        <!-- Row 1: Badges -->
        <div class="kpi-header__badges-row">
          <KPIStatusBadge :status="status" />
          <div v-if="showVersion" class="kpi-header__version-badge">v.{{ version }}</div>
          <KPIPendingChangesBadge :has-pending-changes="has_pending_changes" />
          <div class="kpi-header__format-label">{{ format }}</div>
          <div class="kpi-header__precision-label">
            <img src="@components/assets/decimal-precision.svg" alt="precision" class="kpi-header__precision-icon" />
            <span class="kpi-header__precision-value">1</span>
          </div>
        </div>

        <!-- Row 2: Title and Subcomponents -->
        <div class="kpi-header__info-row">
          <h1 class="kpi-header__title">
            {{ name }} <span class="kpi-header__metric">({{ metric }})</span>
          </h1>
          <div class="kpi-header__subcomponents">
            <TargetsType :type="targets_type" :label="true" />
            <ActualCalculation :type="actual_calculation" :label="true" />
            <KPIDirectionMessageInHeader :less_is_favorable="less_is_favorable" />
          </div>
        </div>
      </div>

      <div class="kpi-header__right">
        <div class="kpi-header__actions">
          <ChatterIcon :unread="0" />
          <MenuIcon :hideTip="true" />
        </div>
      </div>
    </div>
  </header>
</template>

<script setup>
import { computed } from "vue";
import ExBreadcrumbs from "@components/breadcrumbs/ExBreadcrumbs.vue";
import Icon from "@components/icons/Icon.vue";
import KPIStatusBadge from "@components/KPIStatusBadge/KPIStatusBadge.vue";
import KPIPendingChangesBadge from "@components/KPIPendingChangesBadge/KPIPendingChangesBadge.vue";
import TargetsType from "@components/TargetsType/TargetsType.vue";
import ActualCalculation from "@components/ActualCalculation/ActualCalculation.vue";
import KPIDirectionMessageInHeader from "@components/KPIDirectionMessageInHeader/KPIDirectionMessageInHeader.vue";
import ChatterIcon from "@components/ChatterIcon/ChatterIcon.vue";
import MenuIcon from "@components/MenuIcon/MenuIcon.vue";

const props = defineProps({
  breadcrumbsList: {
    type: Array,
    default: () => [],
  },
  status: {
    type: String,
    required: true,
    validator: (v) => ["draft", "published", "active", "retired", "archived"].includes(String(v).toLowerCase()),
  },
  version: {
    type: Number,
    default: null,
  },
  has_pending_changes: {
    type: Boolean,
    default: false,
  },
  format: {
    type: String,
    required: true,
    validator: (v) => ["Currency", "Percent", "Numeric"].includes(v),
  },
  name: {
    type: String,
    required: true,
  },
  metric: {
    type: String,
    required: true,
  },
  targets_type: {
    type: String,
    required: true,
    validator: (v) => ["Fixed", "Prorated", "Progress"].includes(v),
  },
  actual_calculation: {
    type: String,
    required: true,
    validator: (v) => ["Last", "Average"].includes(v),
  },
  less_is_favorable: {
    type: Boolean,
    default: false,
  },
});

const isDraft = computed(() => props.status.toLowerCase() === "draft");
const showVersion = computed(() => !isDraft.value && props.version !== null);
</script>

<style scoped>
.kpi-header {
  padding: 16px 24px;
  font-family: Inter;
}

.kpi-header__breadcrumbs {
  margin-bottom: 12px;
}

.kpi-header__main {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  position: relative;
  top: 15px;
}

.kpi-header__left {
  flex-shrink: 0;
}

.kpi-header__icon-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 60px;
  height: 60px;
  margin-top: 0px;
}

.kpi-header__kpi-icon {
  width: 60px;
  height: 60px;
}

.kpi-header__center {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0px;
  position: relative;
  left: -5px;
}

.kpi-header__badges-row {
  display: flex;
  align-items: center;
  gap: 12px;
  height: 28px;
}

.kpi-header__version-badge {
  background: #F9FAFB;
  border: 1px solid #EAECF0;
  border-radius: 6px;
  padding: 2px 8px;
  font-weight: 500;
  font-size: 12px;
  line-height: 18px;
  color: #344054;
}

.kpi-header__format-label {
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  color: #344054;
}

.kpi-header__precision-label {
  display: flex;
  align-items: center;
  gap: 4px;
}

.kpi-header__precision-value {
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  color: #101828;
}

.kpi-header__info-row {
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;
}

.kpi-header__title {
  font-weight: 600;
  font-size: 24px;
  line-height: 32px;
  color: #101828;
  margin: 0;
}

.kpi-header__subcomponents {
  display: flex;
  align-items: center;
  gap: 16px;
}

.kpi-header__right {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  padding-top: 14px;
}

.kpi-header__actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

/* Subcomponent Overrides to match design */
:deep(.kpi-direction-message-in-header) {
  width: auto;
  height: auto;
  border: none;
  background: transparent;
  padding: 0;
}
</style>
